/**
 * Session restore, deduplicated.
 *
 * ## Analogía
 * En la puerta del edificio hay una pizarra con una sola nota
 * adhesiva: "averiguar si el 3°B sigue aquí — pendiente". El primer
 * guardia que pasa arranca la nota y va a la puerta del 3°B a
 * verificar. Si llega un segundo guardia mientras el primero sigue
 * caminando, ve que la nota no está y que el primero sigue caminando;
 * espera a que vuelva con la respuesta. Cuando el primero vuelve y
 * dice "el 3°B está vacío", los dos guardias anotan lo mismo. La
 * pizarra se borra al cambiar el turno (logout o recarga de página)
 * y empieza una nueva jornada con una hoja en blanco.
 *
 * ## Cómo se traduce
 * | Pieza del edificio            | Pieza del código                                    |
 * | ----------------------------- | ---------------------------------------------------- |
 * | Pizarra con la nota adhesiva  | Módulo `restore.ts` con `let promise: ... | null`     |
 * | `??=` que pega la nota         | `promise ??= authApi.me()...` se ejecuta una vez    |
 * | Guardia 1 arrancando la nota    | Mount 1 de `<AuthProvider>` (StrictMode en dev)     |
 * | Guardia 2 esperando al 1       | Mount 2 (StrictMode)                                |
 * | Verificar el 3°B               | `GET /auth/me/`                                     |
 * | Pizarra borrada al recargar    | El módulo se re-evalúa y `promise` arranca en null |
 *
 * ## Detalle técnico
 * On a cold start the in-memory access token is gone. The refresh
 * cookie may still be valid. `<AuthProvider>` asks the API who the
 * caller is and decides whether the session is alive. In dev React
 * mounts the component twice (StrictMode runs mount → cleanup → mount
 * to surface non-idempotent effects), and a naïve `await authApi.me()`
 * inside the effect fires two HTTP requests per page load: one of them
 * settles against a revoked token if the refresh was just rotated.
 *
 * The fix is one shared promise for the lifetime of the page. Whatever
 * the request's outcome is — user, error — every consumer awaits the
 * same future. A full page reload re-evaluates the module, so the
 * promise resets and the next cold start starts fresh.
 */
import type { CurrentUser } from '@/api/types';

import { authApi } from './authApi';

let promise: Promise<CurrentUser | null> | null = null;

/**
 * Return the in-flight (or already-settled) restore promise. Safe to
 * call from any number of effects during the same page load.
 */
export function restoreSession(): Promise<CurrentUser | null> {
  promise ??= authApi
    .me()
    .then((user): CurrentUser | null => user)
    .catch((): null => null);
  return promise;
}

/**
 * Drop the cached promise. Reserved for tests and for an explicit
 * "logout has invalidated the session" path if one is added later;
 * the normal flow lets the module-scoped promise expire with the
 * page reload.
 */
export function _resetRestoreForTests(): void {
  promise = null;
}
